import type { GetStaticProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { JournalPost } from '@/types'

import Image from 'next/image'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SplitWords } from '@/components/generals/split-words'
import { JournalCard } from '@/components/journal/journal-card'
import { useGsap } from '@/hooks/use-gsap'
import { getJournalPosts } from '@/lib/journal'

interface JournalIndexProps {
    posts?: JournalPost[]
}

const JournalIndex: FC<JournalIndexProps> = ({ posts = [] }): ReactNode => {
    const scope = useGsap<HTMLDivElement>(({ gsap, select, reduced }) => {
        if (reduced) return

        const intro = gsap.timeline({ defaults: { ease: 'nuvea' } })

        intro
            .from(select('[data-journal-title] .reveal-mask > span'), {
                yPercent: 115,
                duration: 1.15,
                stagger: 0.06
            })
            .from(
                select('[data-journal-copy] > *'),
                { y: 24, opacity: 0, duration: 0.9, stagger: 0.09 },
                '-=0.7'
            )
            .fromTo(
                select('[data-journal-hero]'),
                { clipPath: 'inset(0% 0% 100% 0% round 1rem)' },
                { clipPath: 'inset(0% 0% 0% 0% round 1rem)', duration: 1.4 },
                '-=0.6'
            )

        // The hero keeps drifting behind its frame as the archive scrolls past.
        gsap.fromTo(
            select('[data-journal-hero] img'),
            { yPercent: -6, scale: 1.12 },
            {
                yPercent: 6,
                scale: 1.12,
                ease: 'none',
                scrollTrigger: {
                    trigger: select('[data-journal-hero]')[0],
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 0.8
                }
            }
        )

        // The lead story unmasks first, then the grid deals itself out.
        const lead = select('[data-journal-lead]')[0]

        if (lead) {
            gsap.fromTo(
                lead,
                { clipPath: 'inset(0% 0% 100% 0% round 1rem)' },
                {
                    clipPath: 'inset(0% 0% 0% 0% round 1rem)',
                    duration: 1.4,
                    ease: 'nuvea',
                    scrollTrigger: { trigger: lead, start: 'top 88%' }
                }
            )
        }

        select('[data-journal-tile]').forEach((tile, index) => {
            gsap.from(tile, {
                y: 56,
                opacity: 0,
                duration: 1,
                delay: (index % 2) * 0.08,
                ease: 'nuvea',
                scrollTrigger: { trigger: tile, start: 'top 90%' }
            })
        })
    }, [posts.length])

    const [lead, ...rest] = posts

    return (
        <Layout>
            <Seo
                title='Journal'
                description='Trial results, formulation notes and the occasional argument about what skincare is actually
                for.'
                ogImage={lead?.coverImage}
            />

            <div ref={scope}>
                <section
                    id='journal-intro'
                    className='shell-x mx-auto w-full max-w-[1760px] pt-10 md:pt-16'
                >
                    <SplitWords
                        data-journal-title=''
                        as='h1'
                        lines={['Worth reading slowly.']}
                        className='type-display text-ink'
                    />

                    <div className='mt-10 grid grid-cols-1 gap-8 md:mt-14 md:grid-cols-[10rem_1fr] md:gap-12 lg:grid-cols-[14rem_minmax(0,56ch)_1fr]'>
                        <p className='type-label h-fit w-fit rounded-full bg-sage px-3 py-1.5 text-ink'>
                            The journal
                        </p>

                        <div data-journal-copy='' className='flex flex-col gap-6'>
                            <p className='type-lead text-ink'>
                                Insights, research, and stories exploring the science of skincare and
                                its impact on health, wellness, and daily life.
                            </p>

                            <p className='type-copy text-slate-soft'>
                                Written by the two people who make the product, not by a content team.
                            </p>
                        </div>
                    </div>

                    <div
                        data-journal-hero=''
                        className='relative mt-12 aspect-[3/2] w-full overflow-hidden rounded-2xl bg-shell md:mt-16 md:aspect-[21/10]'
                    >
                        <Image
                            src='/images/journal-banner.png'
                            alt='A fingertip pressing a small amount of cream into the skin beneath the eye'
                            fill
                            priority
                            sizes='100vw'
                            className='object-cover will-change-transform'
                        />
                    </div>
                </section>

                <section
                    id='journal-archive'
                    className='shell-x mx-auto w-full max-w-[1760px] pt-12 pb-24 md:pt-16 md:pb-32'
                >
                    <h2 className='sr-only'>
                        All journal entries
                    </h2>

                    {posts.length === 0 ? (
                        <p className='type-body rounded-2xl border border-stone-line bg-shell px-6 py-16 text-center text-slate-soft'>
                            New entries are on their way. Check back shortly.
                        </p>
                    ) : (
                        <>
                            <div data-journal-lead=''>
                                <JournalCard post={lead} variant='featured' priority />
                            </div>

                            <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-2'>
                                {rest.map((post) => (
                                    <div key={post.id} data-journal-tile=''>
                                        <JournalCard post={post} />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </section>
            </div>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<JournalIndexProps> = async () => {
    try {
        const posts = await getJournalPosts()

        return {
            props: { posts },
            revalidate: 300
        }
    } catch {
        return {
            props: { posts: [] },
            revalidate: 60
        }
    }
}

export default JournalIndex
